"use client";

import { Box, type BoxProps } from "@chakra-ui/react";

export type ContainerWidth = "narrow" | "default" | "wide";

interface PageContainerProps extends Omit<BoxProps, "maxW"> {
  /**
   * Container width variant
   * - narrow: 680px (forms, wizards, single-column content)
   * - default: 1100px (standard dashboard content)
   * - wide: 1200px (data tables, logs, wide content)
   */
  width?: ContainerWidth;
  /**
   * Whether to center the container horizontally
   * @default true
   */
  centered?: boolean;
  /**
   * Custom max width (overrides width variant)
   */
  customMaxW?: string;
}

const WIDTH_MAP: Record<ContainerWidth, string> = {
  narrow: "var(--content-max-width-narrow)",
  default: "var(--content-max-width)",
  wide: "var(--content-max-width-wide)",
};

/**
 * Standardized page container component for consistent layout widths.
 * Uses CSS variables defined in globals.css for maintainability.
 */
export function PageContainer({
  width = "default",
  centered = true,
  customMaxW,
  children,
  ...props
}: PageContainerProps) {
  return (
    <Box
      maxW={customMaxW || WIDTH_MAP[width]}
      mx={centered ? "auto" : undefined}
      w="full"
      {...props}
    >
      {children}
    </Box>
  );
}
